import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import PerfDiagnosticsBootstrap from './components/PerfDiagnosticsBootstrap';
import { Toaster } from './components/ui/sonner';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import SuperAdminRoute from './components/SuperAdminRoute';

// Auth Pages — eager (giriş öncesi)
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import Privacy from './pages/Privacy';
import Kvkk from './pages/legal/Kvkk';
import Terms from './pages/legal/Terms';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Main Pages — Dashboard eager (giriş sonrası ilk ekran); diğerleri lazy.
import Dashboard from './pages/Dashboard';
const Activate = lazy(() => import('./pages/Activate'));
const Collection = lazy(() => import('./pages/Collection'));
const CharacterDetail = lazy(() => import('./pages/CharacterDetail'));
const Profile = lazy(() => import('./pages/Profile'));
const ProfileAppearancePage = lazy(() => import('./pages/profile/ProfileAppearancePage'));
const ProfileAchievementsPage = lazy(() => import('./pages/profile/ProfileAchievementsPage'));
const ProfileAccountPage = lazy(() => import('./pages/profile/ProfileAccountPage'));
const ProfileFriendsPage = lazy(() => import('./pages/profile/ProfileFriendsPage'));
const ProfileWishlistPage = lazy(() => import('./pages/profile/ProfileWishlistPage'));
const ProfileTradesPage = lazy(() => import('./pages/profile/ProfileTradesPage'));
const ProfileGuildsPage = lazy(() => import('./pages/profile/ProfileGuildsPage'));
const ProfileCosmeticsPage = lazy(() => import('./pages/profile/ProfileCosmeticsPage'));
const ProfileSettingsPage = lazy(() => import('./pages/profile/ProfileSettingsPage'));
const ProfileLifetimePage = lazy(() => import('./pages/profile/ProfileLifetimePage'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const HallOfFame = lazy(() => import('./pages/HallOfFame'));
const PublicProfile = lazy(() => import('./pages/PublicProfile'));
const Inventory = lazy(() => import('./pages/Inventory'));

// Admin Pages — tüm admin yolu lazy (oyuncuya hiç yüklenmez)
import AdminLayout from './components/admin/AdminLayout';
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminSeries = lazy(() => import('./pages/admin/AdminSeries'));
const AdminCharacters = lazy(() => import('./pages/admin/AdminCharacters'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminActivationCodes = lazy(() => import('./pages/admin/AdminActivationCodes'));
const AdminAudit = lazy(() => import('./pages/admin/AdminAudit'));
const AdminBoxes = lazy(() => import('./pages/admin/AdminBoxes'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminActivityFeedSettings = lazy(() => import('./pages/admin/AdminActivityFeedSettings'));
const AdminSystemDiagnostics = lazy(() => import('./pages/admin/AdminSystemDiagnostics'));
const AdminCardTemplates = lazy(() => import('./pages/admin/AdminCardTemplates'));
const AdminAvatars = lazy(() => import('./pages/admin/AdminAvatars'));
const AdminMissions = lazy(() => import('./pages/admin/AdminMissions'));
const AdminProgressionSettings = lazy(() => import('./pages/admin/AdminProgressionSettings'));

import './App.css';
import { initCapacitorAndroidBackButton } from './utils/androidBackButton';
import { initCapacitorStatusBar } from './utils/statusBar';
import CapacitorOAuthBridge from './components/CapacitorOAuthBridge';
import AvatarOnboardingModal from './components/avatar/AvatarOnboardingModal';
import ClientConfigGate from './components/ClientConfigGate';
import LegalAcceptanceGate from './components/LegalAcceptanceGate';
import OnboardingTutorial from './components/OnboardingTutorial';
import WhatsNewModal from './components/WhatsNewModal';
import PlayerAppShell from './components/PlayerAppShell';


function RouteSuspenseFallback() {
  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-violet-500" />
    </div>
  );
}


function AppRouter() {
  return (
    <Suspense fallback={<RouteSuspenseFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/kvkk" element={<Kvkk />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Oyuncu — ortak kabuk: masaüstü üst nav + mobil alt nav */}
        <Route
          element={
            <ProtectedRoute>
              <PlayerAppShell />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/activate" element={<Activate />} />
          <Route path="/collection" element={<Collection />} />
          <Route path="/character/:characterId" element={<CharacterDetail />} />
          {/* Backward compat: eski linkler */}
          <Route path="/characters/:characterId" element={<CharacterDetail />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/leaderboard/hall-of-fame" element={<HallOfFame />} />
          <Route path="/u/:userId" element={<PublicProfile />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/appearance" element={<ProfileAppearancePage />} />
          <Route path="/profile/achievements" element={<ProfileAchievementsPage />} />
          <Route path="/profile/friends" element={<ProfileFriendsPage />} />
          <Route path="/profile/wishlist" element={<ProfileWishlistPage />} />
          <Route path="/profile/trades" element={<ProfileTradesPage />} />
          <Route path="/profile/guilds" element={<ProfileGuildsPage />} />
          <Route path="/profile/cosmetics" element={<ProfileCosmeticsPage />} />
          <Route path="/profile/lifetime" element={<ProfileLifetimePage />} />
          <Route path="/profile/settings" element={<ProfileSettingsPage />} />
          <Route path="/profile/account" element={<ProfileAccountPage />} />
          <Route path="/inventory" element={<Inventory />} />
        </Route>

        {/* Eski URL uyumluluğu: /qr-scanner ayrı sayfa değil; /activate ile aynı akış (manuel kod / kamera OCR; QR görüntü okuyucu endpoint’i yok). */}
        <Route path="/qr-scanner" element={<Navigate to="/activate" replace />} />

        {/* Admin Routes - sadece admin/superadmin rolü */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="series" element={<AdminSeries />} />
          <Route path="characters" element={<AdminCharacters />} />
          <Route path="card-templates" element={<AdminCardTemplates />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="avatars" element={<AdminAvatars />} />
          <Route path="missions" element={<AdminMissions />} />
          <Route path="progression" element={<AdminProgressionSettings />} />
          <Route path="activation-codes" element={<AdminActivationCodes />} />
          {/* Eski admin URL: /admin/qr ayrı ekran değil; aktivasyon kodları listesine yönlendirir */}
          <Route path="qr" element={<Navigate to="/admin/activation-codes" replace />} />
          <Route path="boxes" element={<AdminBoxes />} />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="activity-feed-settings" element={<AdminActivityFeedSettings />} />
          <Route path="system-diagnostics" element={<AdminSystemDiagnostics />} />
          <Route
            path="settings"
            element={
              <SuperAdminRoute>
                <AdminSettings />
              </SuperAdminRoute>
            }
          />
        </Route>

        {/* Default Route */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

function AvatarOnboardingGate() {
  const { user, loading, avatarOnboardingChecked, avatarOnboardingRequired } = useAuth();
  const waitingStatus = !loading && !!user && !avatarOnboardingChecked;
  const open = !loading && !!user && avatarOnboardingChecked && avatarOnboardingRequired;
  if (waitingStatus) {
    return (
      <div className="fixed inset-0 z-[115] bg-black/80 backdrop-blur-sm flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-violet-500" />
          <p className="mt-3 text-sm text-zinc-400">Hesap hazırlanıyor...</p>
        </div>
      </div>
    );
  }
  return <AvatarOnboardingModal open={open} />;
}

function App() {
  useEffect(() => {
    let removeListener = () => {};
    let cancelled = false;
    initCapacitorStatusBar();
    initCapacitorAndroidBackButton().then((remove) => {
      if (!cancelled && typeof remove === 'function') removeListener = remove;
    });
    return () => {
      cancelled = true;
      removeListener();
    };
  }, []);

  return (
    <ClientConfigGate>
      <BrowserRouter>
        <AuthProvider>
          <CapacitorOAuthBridge />
          <PerfDiagnosticsBootstrap />
          <div className="App">
            <AppRouter />
            <LegalAcceptanceGate />
            <AvatarOnboardingGate />
            <OnboardingTutorial />
            <WhatsNewModal />
            <Toaster position="top-right" theme="dark" />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </ClientConfigGate>
  );
}

export default App;
